import Image from "next/image";
import type { CSSProperties, ReactNode } from "react";
import { buttonClass } from "./design";
import { ReviewListBlock } from "./ReviewListBlock";
import { TemplateLeadForm } from "./TemplateLeadForm";
import { DefaultTemplateLink } from "./templateLink";
import type { ResolvedTemplateDesign, SiteLeadForm, SitePageContent, TemplateContentNode, TemplateLinkComponent } from "./types";

function attrString(attrs: Record<string, unknown> | undefined, key: string, fallback = "") {
    const value = attrs?.[key];
    return typeof value === "string" ? value : fallback;
}

function attrNumber(attrs: Record<string, unknown> | undefined, key: string, fallback = 0) {
    const value = attrs?.[key];
    return typeof value === "number" ? value : fallback;
}

function attrFaqItems(attrs: Record<string, unknown> | undefined) {
    const value = attrs?.items;
    if (!Array.isArray(value)) return [];

    return value.filter((item): item is { question: string; answer: string } => (
        typeof item === "object" &&
        item !== null &&
        typeof (item as { question?: unknown }).question === "string" &&
        typeof (item as { answer?: unknown }).answer === "string"
    ));
}

function attrFeatureItems(attrs: Record<string, unknown> | undefined) {
    const value = attrs?.items;
    if (!Array.isArray(value)) return [];

    return value.flatMap((item): Array<{ title: string; text: string }> => {
        if (typeof item === "string") {
            return [{ title: item, text: "" }];
        }

        if (
            typeof item === "object" &&
            item !== null &&
            typeof (item as { title?: unknown }).title === "string"
        ) {
            const candidate = item as { title: string; text?: unknown };

            return [{ title: candidate.title, text: typeof candidate.text === "string" ? candidate.text : "" }];
        }

        return [];
    });
}

type CanvasLayer = {
    id: string;
    kind: "text" | "button" | "badge" | "box" | "stars";
    text: string;
    x: string;
    y: string;
    width: string;
    textColor: string;
    backgroundColor: string;
    fontSize: string;
    zIndex: string;
};

function attrCanvasLayers(attrs: Record<string, unknown> | undefined): CanvasLayer[] {
    const value = attrs?.layers;
    if (!Array.isArray(value)) return [];

    return value.flatMap((item, index): CanvasLayer[] => {
        if (typeof item !== "object" || item === null) {
            return [];
        }

        const candidate = item as Partial<CanvasLayer>;

        return [{
            id: typeof candidate.id === "string" && candidate.id !== "" ? candidate.id : `layer-${index + 1}`,
            kind: ["text", "button", "badge", "box", "stars"].includes(candidate.kind ?? "") ? candidate.kind as CanvasLayer["kind"] : "text",
            text: typeof candidate.text === "string" ? candidate.text : "",
            x: typeof candidate.x === "string" ? candidate.x : "10%",
            y: typeof candidate.y === "string" ? candidate.y : "10%",
            width: typeof candidate.width === "string" ? candidate.width : "auto",
            textColor: typeof candidate.textColor === "string" ? candidate.textColor : "",
            backgroundColor: typeof candidate.backgroundColor === "string" ? candidate.backgroundColor : "",
            fontSize: typeof candidate.fontSize === "string" ? candidate.fontSize : "",
            zIndex: typeof candidate.zIndex === "string" ? candidate.zIndex : "",
        }];
    });
}

function contentBlockStyle(attrs: Record<string, unknown> | undefined): CSSProperties {
    const backgroundColor = attrString(attrs, "backgroundColor");
    const textColor = attrString(attrs, "textColor");
    const offsetX = attrString(attrs, "offsetX");
    const offsetY = attrString(attrs, "offsetY");
    const width = attrString(attrs, "width");
    const zIndex = attrString(attrs, "zIndex");
    const placement = attrString(attrs, "contentPlacement");

    return {
        ...(backgroundColor ? { backgroundColor, "--site-surface": backgroundColor } as CSSProperties : {}),
        ...(textColor ? { color: textColor, "--site-text": textColor, "--site-muted": textColor } as CSSProperties : {}),
        ...(width ? { width, maxWidth: "100%" } : {}),
        ...((offsetX || offsetY || zIndex) ? { position: "relative" as const } : {}),
        ...((offsetX || offsetY) ? { transform: `translate(${offsetX || "0"}, ${offsetY || "0"})` } : {}),
        ...(zIndex ? { zIndex: Number(zIndex) || undefined } : {}),
        ...(placement === "center" ? { textAlign: "center" as const } : {}),
        ...(placement === "right" ? { textAlign: "right" as const } : {}),
    };
}

function canvasLayerStyle(layer: CanvasLayer): CSSProperties {
    return {
        position: "absolute",
        left: layer.x,
        top: layer.y,
        width: layer.width && layer.width !== "auto" ? layer.width : undefined,
        color: layer.textColor || undefined,
        backgroundColor: layer.backgroundColor || undefined,
        fontSize: layer.fontSize || undefined,
        zIndex: layer.zIndex ? Number(layer.zIndex) || undefined : undefined,
    };
}

function renderCanvasLayer(layer: CanvasLayer): ReactNode {
    const className = [
        "absolute",
        layer.kind === "button" ? "rounded-full px-5 py-3 text-sm font-bold shadow-sm" : "",
        layer.kind === "badge" ? "rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide shadow-sm" : "",
        layer.kind === "box" ? "min-h-24 rounded-[var(--site-radius)] border border-[var(--site-border)] shadow-sm" : "",
        layer.kind === "text" ? "font-bold leading-tight" : "",
        layer.kind === "stars" ? "font-bold tracking-wide" : "",
    ].filter(Boolean).join(" ");

    if (layer.kind === "box") {
        return <div key={layer.id} className={className} style={canvasLayerStyle(layer)} />;
    }

    return <div key={layer.id} className={className} style={canvasLayerStyle(layer)}>{layer.text}</div>;
}

function renderBlockLayers(attrs: Record<string, unknown> | undefined): ReactNode {
    const layers = attrCanvasLayers(attrs);

    return layers.length > 0 ? layers.map(renderCanvasLayer) : null;
}

function renderText(node: TemplateContentNode, key: string, LinkComponent: TemplateLinkComponent) {
    let element: ReactNode = node.text ?? "";

    for (const mark of node.marks ?? []) {
        if (mark.type === "bold") element = <strong key={`${key}-bold`}>{element}</strong>;
        if (mark.type === "italic") element = <em key={`${key}-italic`}>{element}</em>;
        if (mark.type === "underline") element = <u key={`${key}-underline`}>{element}</u>;
        if (mark.type === "strike") element = <s key={`${key}-strike`}>{element}</s>;
        if (mark.type === "link") {
            const href = attrString(mark.attrs, "href", "#");
            element = href.startsWith("/") ? (
                <LinkComponent key={`${key}-link`} href={href}>{element}</LinkComponent>
            ) : (
                <a key={`${key}-link`} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noreferrer" : undefined}>
                    {element}
                </a>
            );
        }
        if (mark.type === "textStyle") {
            const color = attrString(mark.attrs, "color");
            element = <span key={`${key}-color`} style={color ? { color } : undefined}>{element}</span>;
        }
        if (mark.type === "highlight") {
            const color = attrString(mark.attrs, "color");
            element = <mark key={`${key}-highlight`} style={color ? { backgroundColor: color } : undefined}>{element}</mark>;
        }
    }

    return element;
}

function renderNodes(
    nodes: TemplateContentNode[] | undefined,
    page: SitePageContent,
    design: ResolvedTemplateDesign,
    prefix: string,
    LinkComponent: TemplateLinkComponent,
    leadForm?: SiteLeadForm | null,
    readOnly = false,
): ReactNode {
    return nodes?.map((node, index) => renderNode(
        node,
        page,
        design,
        `${prefix}-${index}`,
        LinkComponent,
        leadForm,
        readOnly,
    )) ?? null;
}

function renderLink(href: string, className: string, children: ReactNode, LinkComponent: TemplateLinkComponent) {
    return href.startsWith("/") ? (
        <LinkComponent href={href} className={className}>{children}</LinkComponent>
    ) : (
        <a href={href} className={className} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noreferrer" : undefined}>
            {children}
        </a>
    );
}

function renderNode(
    node: TemplateContentNode,
    page: SitePageContent,
    design: ResolvedTemplateDesign,
    key: string,
    LinkComponent: TemplateLinkComponent,
    leadForm?: SiteLeadForm | null,
    readOnly = false,
): ReactNode {
    const attrs = node.attrs;

    if (node.type === "text") return renderText(node, key, LinkComponent);
    if (node.type === "hardBreak") return <br key={key} />;
    if (node.type === "paragraph") return <p key={key}>{renderNodes(node.content, page, design, key, LinkComponent, leadForm, readOnly)}</p>;
    if (node.type === "heading") {
        const level = Math.min(Math.max(attrNumber(attrs, "level", 2), 1), 5);
        const children = renderNodes(node.content, page, design, key, LinkComponent, leadForm, readOnly);
        if (level === 1) return <h1 key={key}>{children}</h1>;
        if (level === 2) return <h2 key={key}>{children}</h2>;
        if (level === 3) return <h3 key={key}>{children}</h3>;
        if (level === 4) return <h4 key={key}>{children}</h4>;
        return <h5 key={key}>{children}</h5>;
    }
    if (node.type === "bulletList") return <ul key={key}>{renderNodes(node.content, page, design, key, LinkComponent, leadForm, readOnly)}</ul>;
    if (node.type === "orderedList") return <ol key={key}>{renderNodes(node.content, page, design, key, LinkComponent, leadForm, readOnly)}</ol>;
    if (node.type === "listItem") return <li key={key}>{renderNodes(node.content, page, design, key, LinkComponent, leadForm, readOnly)}</li>;
    if (node.type === "blockquote") return <blockquote key={key}>{renderNodes(node.content, page, design, key, LinkComponent, leadForm, readOnly)}</blockquote>;
    if (node.type === "horizontalRule") return <hr key={key} />;
    if (node.type === "image") {
        const src = attrString(attrs, "src");
        if (!src) return null;

        return (
            <figure key={key} className="my-8">
                <Image
                    src={src}
                    alt={attrString(attrs, "alt")}
                    width={1200}
                    height={675}
                    className="w-full rounded-[var(--site-radius)] border border-[var(--site-border)]"
                    unoptimized
                />
                {attrString(attrs, "caption") ? <figcaption className="mt-2 text-sm text-[var(--site-muted)]">{attrString(attrs, "caption")}</figcaption> : null}
            </figure>
        );
    }
    if (node.type === "heroBlock") {
        const primaryUrl = attrString(attrs, "primaryUrl", "#");
        const secondaryUrl = attrString(attrs, "secondaryUrl", "#");
        const secondaryLabel = attrString(attrs, "secondaryLabel");

        return (
            <section key={key} className="not-prose relative my-10 rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 sm:p-10" style={contentBlockStyle(attrs)}>
                {attrString(attrs, "eyebrow") ? <p className="text-sm font-semibold uppercase tracking-wide text-[var(--site-primary)]">{attrString(attrs, "eyebrow")}</p> : null}
                <h1 className="mt-3 font-[var(--site-heading-font)] text-4xl font-bold leading-tight text-[var(--site-text)]">{attrString(attrs, "title", page.title)}</h1>
                {attrString(attrs, "text") ? <p className="mt-4 max-w-2xl text-base leading-7 text-[var(--site-muted)]">{attrString(attrs, "text")}</p> : null}
                <div className="mt-6 flex flex-wrap gap-3">
                    {renderLink(primaryUrl, `px-5 py-3 text-sm font-semibold ${buttonClass(design.components.buttons)}`, attrString(attrs, "primaryLabel", "Kontaktovat"), LinkComponent)}
                    {secondaryLabel ? renderLink(secondaryUrl, "rounded-[var(--site-radius)] border border-[var(--site-border)] px-5 py-3 text-sm font-semibold text-[var(--site-text)]", secondaryLabel, LinkComponent) : null}
                </div>
                {renderBlockLayers(attrs)}
            </section>
        );
    }
    if (node.type === "featureGridBlock") {
        const items = attrFeatureItems(attrs);

        return (
            <section key={key} className="not-prose relative my-10" style={contentBlockStyle(attrs)}>
                <div className="max-w-2xl">
                    <h2 className="font-[var(--site-heading-font)] text-3xl font-bold text-[var(--site-text)]">{attrString(attrs, "title", "Co pro vás zajistíme")}</h2>
                    {attrString(attrs, "text") ? <p className="mt-3 text-base leading-7 text-[var(--site-muted)]">{attrString(attrs, "text")}</p> : null}
                </div>
                <div className="mt-6 grid gap-4 md:grid-cols-3">
                    {items.map((item, index) => (
                        <article key={`${key}-${index}`} className="rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-5">
                            <h3 className="font-[var(--site-heading-font)] text-lg font-bold text-[var(--site-text)]">{item.title}</h3>
                            <p className="mt-2 text-sm leading-6 text-[var(--site-muted)]">{item.text}</p>
                        </article>
                    ))}
                </div>
                {renderBlockLayers(attrs)}
            </section>
        );
    }
    if (node.type === "testimonialBlock") {
        return (
            <section key={key} className="not-prose relative my-10 rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 sm:p-8" style={contentBlockStyle(attrs)}>
                <blockquote className="font-[var(--site-heading-font)] text-2xl font-bold leading-snug text-[var(--site-text)]">
                    “{attrString(attrs, "quote")}”
                </blockquote>
                <p className="mt-5 text-sm font-semibold text-[var(--site-text)]">{attrString(attrs, "author")}</p>
                {attrString(attrs, "role") ? <p className="mt-1 text-sm text-[var(--site-muted)]">{attrString(attrs, "role")}</p> : null}
                {renderBlockLayers(attrs)}
            </section>
        );
    }
    if (node.type === "reviewListBlock") {
        const source = attrString(attrs, "source", "currentPage");
        const siteUuid = attrString(attrs, "siteUuid", page.siteUuid ?? "");
        const explicitPageUuid = attrString(attrs, "pageUuid");
        const pageUuid = source === "currentSite" ? null : (explicitPageUuid || page.uuid || null);

        if (!siteUuid || !page.webReviewApiBasePath) {
            return null;
        }

        return (
            <ReviewListBlock
                key={key}
                apiBasePath={page.webReviewApiBasePath}
                siteUuid={siteUuid}
                pageUuid={pageUuid}
                title={attrString(attrs, "title", "Recenze")}
            />
        );
    }
    if (node.type === "canvasBlock") {
        const backgroundImage = attrString(attrs, "backgroundImage");
        const layers = attrCanvasLayers(attrs);

        return (
            <section
                key={key}
                className="not-prose relative my-10 overflow-hidden rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)]"
                style={{
                    ...contentBlockStyle(attrs),
                    minHeight: attrString(attrs, "minHeight", "420px"),
                    backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                }}
            >
                {layers.map(renderCanvasLayer)}
            </section>
        );
    }
    if (node.type === "ctaBlock") {
        return (
            <section key={key} className="not-prose relative my-10 flex flex-col gap-5 rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8" style={contentBlockStyle(attrs)}>
                <div>
                    <h2 className="font-[var(--site-heading-font)] text-2xl font-bold text-[var(--site-text)]">{attrString(attrs, "title", "Připraveno začít?")}</h2>
                    {attrString(attrs, "text") ? <p className="mt-2 text-sm leading-6 text-[var(--site-muted)]">{attrString(attrs, "text")}</p> : null}
                </div>
                {renderLink(attrString(attrs, "buttonUrl", "#"), `shrink-0 px-5 py-3 text-sm font-semibold ${buttonClass(design.components.buttons)}`, attrString(attrs, "buttonLabel", "Kontaktovat"), LinkComponent)}
                {renderBlockLayers(attrs)}
            </section>
        );
    }
    if (node.type === "faqBlock") {
        const items = attrFaqItems(attrs);

        return (
            <section key={key} className="not-prose relative my-10" style={contentBlockStyle(attrs)}>
                <h2 className="font-[var(--site-heading-font)] text-3xl font-bold text-[var(--site-text)]">Často kladené otázky</h2>
                <div className="mt-5 divide-y divide-[var(--site-border)] rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)]">
                    {items.map((item, index) => (
                        <details key={`${key}-${index}`} className="p-5">
                            <summary className="cursor-pointer font-semibold text-[var(--site-text)]">{item.question || "Otázka"}</summary>
                            <p className="mt-3 text-sm leading-6 text-[var(--site-muted)]">{item.answer}</p>
                        </details>
                    ))}
                </div>
                {renderBlockLayers(attrs)}
            </section>
        );
    }
    if (node.type === "galleryBlock") {
        return (
            <section key={key} className="not-prose relative my-10" style={contentBlockStyle(attrs)}>
                <h2 className="font-[var(--site-heading-font)] text-3xl font-bold text-[var(--site-text)]">{attrString(attrs, "title", "Galerie")}</h2>
                {page.imageSlider ? <div className="mt-5">{page.imageSlider}</div> : <p className="mt-3 text-sm text-[var(--site-muted)]">Galerie stránky zatím neobsahuje obrázky.</p>}
                {renderBlockLayers(attrs)}
            </section>
        );
    }
    if (node.type === "formBlock") {
        return (
            <section key={key} className="not-prose relative my-10 rounded-[var(--site-radius)] border border-[var(--site-border)] bg-[var(--site-surface)] p-6" style={contentBlockStyle(attrs)}>
                <h2 className="font-[var(--site-heading-font)] text-2xl font-bold text-[var(--site-text)]">{attrString(attrs, "formName", "Formulář")}</h2>
                {attrString(attrs, "text") ? <p className="mt-2 text-sm leading-6 text-[var(--site-muted)]">{attrString(attrs, "text")}</p> : null}
                <div className="mt-5">
                    <TemplateLeadForm form={leadForm} design={design} readOnly={readOnly} />
                </div>
                {renderBlockLayers(attrs)}
            </section>
        );
    }

    return <div key={key}>{renderNodes(node.content, page, design, key, LinkComponent, leadForm, readOnly)}</div>;
}

export function renderTemplateContent(
    page: SitePageContent,
    design: ResolvedTemplateDesign,
    LinkComponent: TemplateLinkComponent = DefaultTemplateLink,
    leadForm?: SiteLeadForm | null,
    readOnly = false,
) {
    const nodes = page.contentJson?.content;

    if (nodes && nodes.length > 0) {
        return renderNodes(nodes, page, design, "content", LinkComponent, leadForm, readOnly);
    }

    if (page.content) {
        return <div dangerouslySetInnerHTML={{ __html: page.content }} />;
    }

    return <p className="text-[var(--site-muted)]">Stránka zatím nemá vyplněný obsah.</p>;
}
